import { format } from "date-fns"

/**
 * Communication log APIs may return ISO strings, or Unix time in **seconds** (number / numeric string).
 * `new Date(seconds)` is wrong — JS expects milliseconds — which yields dates around Jan 1970.
 *
 * **Angular parity** (`communication-log-list.component.html`): list binds `logdetail.log_datetime` and
 * `logdetail.created_at` separately — they often **differ by design**:
 * - `log_datetime` — when the communication event occurred (user-entered / business time).
 * - `created_at` — when the CRM row was persisted (server timestamp).
 *
 * Display patterns match Angular `date` pipe: `MM/dd/yyyy hh:mm a` and `MM/dd/yyyy hh:mm:ss a`.
 */
export function parseCommunicationLogDate(value: unknown): Date | null {
    if (value == null || value === "") return null

    if (typeof value === "number" && Number.isFinite(value)) {
        const ms = value < 1e12 ? value * 1000 : value
        const d = new Date(ms)
        return Number.isNaN(d.getTime()) ? null : d
    }

    if (typeof value === "string") {
        const t = value.trim()
        if (/^\d+$/.test(t)) {
            const n = Number(t)
            if (!Number.isFinite(n)) return null
            const ms = n < 1e12 ? n * 1000 : n
            const d = new Date(ms)
            return Number.isNaN(d.getTime()) ? null : d
        }
        const d = new Date(t)
        return Number.isNaN(d.getTime()) ? null : d
    }

    return null
}

/** Log Date / Time column — Angular: `date:'MM/dd/yyyy hh:mm a'`. */
export function formatCommunicationLogDateTime(value: unknown): string {
    const d = parseCommunicationLogDate(value)
    return d ? format(d, "MM/dd/yyyy hh:mm a") : ""
}

/** Created Date / Time column — Angular: `date:'MM/dd/yyyy hh:mm:ss a'`. */
export function formatCommunicationLogCreatedAt(value: unknown): string {
    const d = parseCommunicationLogDate(value)
    return d ? format(d, "MM/dd/yyyy hh:mm:ss a") : ""
}
